import { supabase } from "@/integrations/supabase/client";
import { 
  ESCPOS, 
  normalizeForPrinter, 
  getPrinterSettings,
  PrinterSettings,
} from "@/lib/qz-tray";
import { dispatchPrint } from "@/lib/print-dispatcher";
import { fetchPrintTemplate, PrintTemplate } from "@/hooks/usePrintTemplates";
import { getTemplateFormatting } from "@/lib/print-template-formatting";

export interface CancellationData {
  productName: string;
  productId?: string | null;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  reason?: string;
  tableNumber?: string;
  tabNumber?: string;
  cancelledBy: string;
  restaurantId: string;
  restaurantName?: string;
}

/**
 * Format cancellation ticket for kitchen/production
 */
export function formatCancellationKitchenTicket(
  data: CancellationData,
  charsPerLine: number = 48,
  template?: PrintTemplate
): string {
  const date = new Date();
  const timeStr = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  
  const formatting = template ? getTemplateFormatting(template) : null;
  const sepChar = template?.separator_char || "=";
  const sepLightChar = "-";
  const sep = sepChar.repeat(charsPerLine) + "\n";
  const sepLight = sepLightChar.repeat(charsPerLine) + "\n";
  
  const title = "ITEM CANCELADO";

  let ticket = "";
  
  // Header text if configured
  if (template?.header_text) {
    ticket += ESCPOS.ALIGN_CENTER;
    ticket += normalizeForPrinter(template.header_text) + "\n";
    ticket += ESCPOS.ALIGN_LEFT;
  }
  
  ticket += sep;
  ticket += ESCPOS.ALIGN_CENTER;
  
  // Apply title formatting from template
  if (formatting) {
    ticket += formatting.title.start;
  } else {
    ticket += ESCPOS.DOUBLE_SIZE_ON + ESCPOS.BOLD_ON;
  }
  ticket += normalizeForPrinter(title) + "\n";
  if (formatting) {
    ticket += formatting.title.end;
  } else {
    ticket += ESCPOS.DOUBLE_SIZE_OFF + ESCPOS.BOLD_OFF;
  }
  
  ticket += ESCPOS.ALIGN_LEFT;
  ticket += sep;
  
  // Location info
  if (data.tableNumber) {
    ticket += `Mesa: ${data.tableNumber}\n`;
  } else if (data.tabNumber) {
    ticket += `Comanda: ${data.tabNumber}\n`;
  }
  ticket += `Hora: ${timeStr}\n`;
  ticket += sepLight;
  
  // Product with items formatting
  if (formatting) {
    ticket += formatting.items.start;
  } else {
    ticket += ESCPOS.DOUBLE_SIZE_ON + ESCPOS.BOLD_ON;
  }
  ticket += `${normalizeForPrinter(data.productName)}\n`;
  ticket += `QTD: ${data.quantity}\n`;
  if (formatting) {
    ticket += formatting.items.end;
  } else {
    ticket += ESCPOS.DOUBLE_SIZE_OFF + ESCPOS.BOLD_OFF;
  }
  
  if (data.reason) {
    ticket += sepLight;
    ticket += `Motivo: ${normalizeForPrinter(data.reason)}\n`;
  }
  
  ticket += sepLight;
  ticket += ESCPOS.ALIGN_CENTER;
  ticket += ESCPOS.BOLD_ON;
  ticket += "*** NAO PRODUZIR ***\n";
  ticket += ESCPOS.BOLD_OFF;
  ticket += ESCPOS.ALIGN_LEFT;
  ticket += sep;
  
  // Footer
  ticket += ESCPOS.ALIGN_CENTER;
  if (template?.footer_text) {
    ticket += normalizeForPrinter(template.footer_text) + "\n";
  }
  if (template?.show_software_footer !== false) {
    ticket += "Altus Food\n";
  }
  ticket += ESCPOS.ALIGN_LEFT;
  ticket += "\n\n";

  return ticket;
}

/**
 * Format cancellation receipt for cashier (with signature space)
 */
export function formatCancellationReceipt(
  data: CancellationData,
  charsPerLine: number = 48,
  template?: PrintTemplate
): string {
  const date = new Date();
  const dateStr = date.toLocaleDateString("pt-BR");
  const timeStr = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  
  const formatCurrency = (value: number) =>
    normalizeForPrinter(new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value));

  const formatting = template ? getTemplateFormatting(template) : null;
  const sepChar = template?.separator_char || "=";
  const sepLightChar = "-";
  const sep = sepChar.repeat(charsPerLine) + "\n";
  const sepLight = sepLightChar.repeat(charsPerLine) + "\n";
  
  const title = "COMPROVANTE DE CANCELAMENTO";

  let receipt = "";
  
  // Header text if configured
  if (template?.header_text) {
    receipt += ESCPOS.ALIGN_CENTER;
    receipt += normalizeForPrinter(template.header_text) + "\n";
    receipt += ESCPOS.ALIGN_LEFT;
  }
  
  receipt += sep;
  receipt += ESCPOS.ALIGN_CENTER;
  
  // Apply title formatting
  if (formatting) {
    receipt += formatting.title.start;
  }
  receipt += normalizeForPrinter(title) + "\n";
  if (formatting) {
    receipt += formatting.title.end;
  }
  
  receipt += ESCPOS.ALIGN_LEFT;
  receipt += sep;
  
  if (data.restaurantName) {
    receipt += `${normalizeForPrinter(data.restaurantName)}\n`;
  }
  
  receipt += `Data: ${dateStr} ${timeStr}\n`;
  
  if (data.tableNumber) {
    receipt += `Mesa: ${data.tableNumber}\n`;
  } else if (data.tabNumber) {
    receipt += `Comanda: ${data.tabNumber}\n`;
  }
  
  receipt += sepLight;
  
  // Items formatting
  if (formatting) {
    receipt += formatting.items.start;
  } else {
    receipt += ESCPOS.BOLD_ON;
  }
  receipt += `ITEM: ${normalizeForPrinter(data.productName)}\n`;
  if (formatting) {
    receipt += formatting.items.end;
  } else {
    receipt += ESCPOS.BOLD_OFF;
  }
  
  receipt += `Quantidade: ${data.quantity}\n`;
  receipt += `Valor Unit.: ${formatCurrency(data.unitPrice)}\n`;
  
  // Totals formatting
  if (formatting) {
    receipt += formatting.totals.start;
  } else {
    receipt += ESCPOS.DOUBLE_SIZE_ON + ESCPOS.BOLD_ON;
  }
  receipt += `VALOR: ${formatCurrency(data.totalPrice)}\n`;
  if (formatting) {
    receipt += formatting.totals.end;
  } else {
    receipt += ESCPOS.DOUBLE_SIZE_OFF + ESCPOS.BOLD_OFF;
  }
  
  if (data.reason) {
    receipt += sepLight;
    receipt += `Motivo: ${normalizeForPrinter(data.reason)}\n`;
  }
  
  receipt += sepLight;
  receipt += `Cancelado por: ${normalizeForPrinter(data.cancelledBy)}\n`;
  
  receipt += sep;
  receipt += ESCPOS.ALIGN_CENTER;
  receipt += "RESPONSAVEL\n";
  receipt += "\n";
  receipt += "_".repeat(Math.floor(charsPerLine * 0.7)) + "\n";
  receipt += "Assinatura\n";
  receipt += "\n";
  receipt += "_".repeat(Math.floor(charsPerLine * 0.5)) + "\n";
  receipt += "Data/Hora\n";
  receipt += ESCPOS.ALIGN_LEFT;
  receipt += sep;
  
  // Footer
  receipt += ESCPOS.ALIGN_CENTER;
  if (template?.footer_text) {
    receipt += normalizeForPrinter(template.footer_text) + "\n";
  }
  if (template?.show_software_footer !== false) {
    receipt += "Altus Food\n";
  }
  receipt += ESCPOS.ALIGN_LEFT;
  receipt += "\n\n";

  return receipt;
}

/**
 * Format batch cancellation receipt (multiple items in one receipt)
 */
export function formatBatchCancellationReceipt(
  items: CancellationData[],
  charsPerLine: number = 48,
  template?: PrintTemplate
): string {
  if (items.length === 0) return "";
  
  const date = new Date();
  const dateStr = date.toLocaleDateString("pt-BR");
  const timeStr = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  
  const formatCurrency = (value: number) =>
    normalizeForPrinter(new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value));

  const formatting = template ? getTemplateFormatting(template) : null;
  const sepChar = template?.separator_char || "=";
  const sepLightChar = "-";
  const sep = sepChar.repeat(charsPerLine) + "\n";
  const sepLight = sepLightChar.repeat(charsPerLine) + "\n";
  
  const title = "COMPROVANTE DE CANCELAMENTO";

  // Use first item for common data
  const firstItem = items[0];

  let receipt = "";
  
  // Header text if configured
  if (template?.header_text) {
    receipt += ESCPOS.ALIGN_CENTER;
    receipt += normalizeForPrinter(template.header_text) + "\n";
    receipt += ESCPOS.ALIGN_LEFT;
  }
  
  receipt += sep;
  receipt += ESCPOS.ALIGN_CENTER;
  
  // Apply title formatting
  if (formatting) {
    receipt += formatting.title.start;
  }
  receipt += normalizeForPrinter(title) + "\n";
  if (formatting) {
    receipt += formatting.title.end;
  }
  
  receipt += ESCPOS.ALIGN_LEFT;
  receipt += sep;
  
  if (firstItem.restaurantName) {
    receipt += `${normalizeForPrinter(firstItem.restaurantName)}\n`;
  }
  
  receipt += `Data: ${dateStr} ${timeStr}\n`;
  
  if (firstItem.tableNumber) {
    receipt += `Mesa: ${firstItem.tableNumber}\n`;
  } else if (firstItem.tabNumber) {
    receipt += `Comanda: ${firstItem.tabNumber}\n`;
  }
  
  receipt += sepLight;
  
  // Items header with formatting
  if (formatting) {
    receipt += formatting.items.start;
  } else {
    receipt += ESCPOS.BOLD_ON;
  }
  receipt += `ITENS CANCELADOS (${items.length}):\n`;
  if (formatting) {
    receipt += formatting.items.end;
  } else {
    receipt += ESCPOS.BOLD_OFF;
  }
  
  receipt += sepLight;

  let totalValue = 0;
  for (const item of items) {
    if (formatting) {
      receipt += formatting.items.start;
    } else {
      receipt += ESCPOS.BOLD_ON;
    }
    receipt += `${normalizeForPrinter(item.productName)}\n`;
    if (formatting) {
      receipt += formatting.items.end;
    } else {
      receipt += ESCPOS.BOLD_OFF;
    }
    receipt += `  Qtd: ${item.quantity} x ${formatCurrency(item.unitPrice)}\n`;
    receipt += `  Subtotal: ${formatCurrency(item.totalPrice)}\n`;
    if (item.reason) {
      receipt += `  Motivo: ${normalizeForPrinter(item.reason)}\n`;
    }
    receipt += "\n";
    totalValue += item.totalPrice;
  }
  
  receipt += sepLight;
  
  // Totals formatting
  if (formatting) {
    receipt += formatting.totals.start;
  } else {
    receipt += ESCPOS.DOUBLE_SIZE_ON + ESCPOS.BOLD_ON;
  }
  receipt += `TOTAL: ${formatCurrency(totalValue)}\n`;
  if (formatting) {
    receipt += formatting.totals.end;
  } else {
    receipt += ESCPOS.DOUBLE_SIZE_OFF + ESCPOS.BOLD_OFF;
  }
  
  receipt += sepLight;
  receipt += `Cancelado por: ${normalizeForPrinter(firstItem.cancelledBy)}\n`;
  
  receipt += sep;
  receipt += ESCPOS.ALIGN_CENTER;
  receipt += "RESPONSAVEL\n";
  receipt += "\n";
  receipt += "_".repeat(Math.floor(charsPerLine * 0.7)) + "\n";
  receipt += "Assinatura\n";
  receipt += "\n";
  receipt += "_".repeat(Math.floor(charsPerLine * 0.5)) + "\n";
  receipt += "Data/Hora\n";
  receipt += ESCPOS.ALIGN_LEFT;
  receipt += sep;
  
  // Footer
  receipt += ESCPOS.ALIGN_CENTER;
  if (template?.footer_text) {
    receipt += normalizeForPrinter(template.footer_text) + "\n";
  }
  if (template?.show_software_footer !== false) {
    receipt += "Altus Food\n";
  }
  receipt += ESCPOS.ALIGN_LEFT;
  receipt += "\n\n";

  return receipt;
}

/**
 * Print batch cancellation receipt to cashier
 */
export async function printBatchCancellationReceipt(items: CancellationData[]): Promise<boolean> {
  if (items.length === 0) return false;
  
  const restaurantId = items[0].restaurantId;
  
  try {
    // Fetch the template for cancellation receipts
    const template = await fetchPrintTemplate(restaurantId, "cancellation_receipt");
    
    // Try to get printer configured for cancellation_receipt job type first
    const { data: jobTypeData } = await supabase
      .from("printer_job_types")
      .select(`
        printer_id,
        printers!inner (
          id,
          printer_name,
          is_active,
          restaurant_id,
          paper_width,
          chars_per_line,
          auto_cut_enabled,
          cut_type,
          extra_lines_before_cut,
          protocol
        )
      `)
      .eq("job_type", "cancellation_receipt")
      .eq("is_enabled", true);

    // Filter by restaurant and active status
    const validPrinters = jobTypeData?.filter(
      (jt: any) => 
        jt.printers?.restaurant_id === restaurantId && 
        jt.printers?.is_active === true
    );

    let printerInfo: { printerName: string; settings: PrinterSettings } | null = null;

    if (validPrinters && validPrinters.length > 0) {
      const printer = validPrinters[0].printers;
      printerInfo = {
        printerName: printer.printer_name,
        settings: {
          paper_width: (printer.paper_width || "80mm") as "58mm" | "80mm",
          chars_per_line: printer.chars_per_line || 48,
          auto_cut_enabled: printer.auto_cut_enabled ?? true,
          cut_type: (printer.cut_type || "full") as "full" | "partial",
          extra_lines_before_cut: printer.extra_lines_before_cut ?? 3,
          protocol: (printer.protocol || "epson_standard") as "epson_standard" | "bematech_legacy" | "generic",
        },
      };
    } else {
      // Fallback to default printer
      printerInfo = await getDefaultPrinter(restaurantId);
    }
    
    if (!printerInfo) {
      console.log("[Cancellation] No printer configured for batch cancellation receipts");
      return false;
    }
    
    const charsPerLine = printerInfo.settings.chars_per_line;
    
    // Format the batch receipt with template
    const content = formatBatchCancellationReceipt(items, charsPerLine, template);
    
    // Dispatch via centralized dispatcher
    return await dispatchPrint(restaurantId, printerInfo.printerName, content, {
      logType: "cancelamento_comprovante_lote",
      logIdentifier: `${items.length}_itens`,
      locationName: "Cancelamento",
      settings: printerInfo.settings,
    });
  } catch (error) {
    console.error("[Cancellation] Error printing batch receipt:", error);
    return false;
  }
}

/**
 * Get the production location printer for a product
 * Uses printer_id to get the correct printer from the printers table
 * Falls back to job_type routing if printer_id is not set
 * Ignores virtual/PDF printers
 */
async function getProductPrinter(
  productId: string | null | undefined,
  restaurantId?: string
): Promise<string | null> {
  if (!productId) return null;
  
  try {
    const { data, error } = await supabase
      .from("products")
      .select(`
        production_location_id,
        production_locations!inner (
          printer_id,
          printer_name,
          job_type,
          printers (printer_name)
        )
      `)
      .eq("id", productId)
      .single();
    
    if (error || !data?.production_location_id) return null;
    
    // Type assertion for the nested data
    const locationData = data.production_locations as {
      printer_id: string | null;
      printer_name: string;
      job_type: string | null;
      printers: { printer_name: string } | null;
    } | null;
    
    // 1. Prefer printer from printer_id reference (best option)
    if (locationData?.printers?.printer_name) {
      const printerName = locationData.printers.printer_name;
      if (!isPdfPrinter(printerName)) {
        console.log(`[Cancellation] Using printer_id printer: ${printerName}`);
        return printerName;
      }
    }
    
    // 2. If job_type exists, try to find printer by job_type routing
    if (locationData?.job_type && restaurantId) {
      const { data: jobTypeData } = await supabase
        .from("printer_job_types")
        .select(`
          printers!inner (printer_name, restaurant_id, is_active)
        `)
        .eq("job_type", locationData.job_type)
        .eq("is_enabled", true);
      
      const validPrinter = jobTypeData?.find(
        (jt: any) => 
          jt.printers?.restaurant_id === restaurantId && 
          jt.printers?.is_active === true &&
          !isPdfPrinter(jt.printers?.printer_name)
      );
      
      if (validPrinter?.printers?.printer_name) {
        console.log(`[Cancellation] Using job_type "${locationData.job_type}" printer: ${validPrinter.printers.printer_name}`);
        return validPrinter.printers.printer_name;
      }
    }
    
    // 3. Fallback to legacy printer_name only if not a PDF printer
    const legacyName = locationData?.printer_name;
    if (legacyName && !isPdfPrinter(legacyName)) {
      console.log(`[Cancellation] Using legacy printer_name: ${legacyName}`);
      return legacyName;
    }
    
    console.log("[Cancellation] No valid physical printer found for production location");
    return null;
  } catch {
    return null;
  }
}

/**
 * Check if printer name indicates a virtual/PDF printer
 */
function isPdfPrinter(printerName: string | null | undefined): boolean {
  if (!printerName) return false;
  const lower = printerName.toLowerCase();
  return lower.includes("pdf") || lower.includes("xps") || lower.includes("onenote") || lower.includes("fax");
}

/**
 * Get the default printer for a restaurant
 */
async function getDefaultPrinter(restaurantId: string): Promise<{ printerName: string; settings: PrinterSettings } | null> {
  try {
    const { data, error } = await supabase
      .from("printers")
      .select("printer_name, paper_width, chars_per_line, auto_cut_enabled, cut_type, extra_lines_before_cut, protocol")
      .eq("restaurant_id", restaurantId)
      .eq("is_active", true)
      .eq("is_default", true)
      .single();
    
    if (error || !data) {
      // Try to get any active printer
      const { data: anyPrinter } = await supabase
        .from("printers")
        .select("printer_name, paper_width, chars_per_line, auto_cut_enabled, cut_type, extra_lines_before_cut, protocol")
        .eq("restaurant_id", restaurantId)
        .eq("is_active", true)
        .limit(1)
        .single();
      
      if (!anyPrinter) return null;
      
      return {
        printerName: anyPrinter.printer_name,
        settings: {
          paper_width: (anyPrinter.paper_width || "80mm") as "58mm" | "80mm",
          chars_per_line: anyPrinter.chars_per_line || 48,
          auto_cut_enabled: anyPrinter.auto_cut_enabled ?? true,
          cut_type: (anyPrinter.cut_type || "full") as "full" | "partial",
          extra_lines_before_cut: anyPrinter.extra_lines_before_cut ?? 3,
          protocol: (anyPrinter.protocol || "epson_standard") as "epson_standard" | "bematech_legacy" | "generic",
        },
      };
    }
    
    return {
      printerName: data.printer_name,
      settings: {
        paper_width: (data.paper_width || "80mm") as "58mm" | "80mm",
        chars_per_line: data.chars_per_line || 48,
        auto_cut_enabled: data.auto_cut_enabled ?? true,
        cut_type: (data.cut_type || "full") as "full" | "partial",
        extra_lines_before_cut: data.extra_lines_before_cut ?? 3,
        protocol: (data.protocol || "epson_standard") as "epson_standard" | "bematech_legacy" | "generic",
      },
    };
  } catch {
    return null;
  }
}

/**
 * Print cancellation to kitchen/production location
 */
export async function printCancellationToKitchen(data: CancellationData): Promise<boolean> {
  try {
    // Fetch the template for cancellation receipts
    const template = await fetchPrintTemplate(data.restaurantId, "cancellation_receipt");
    
    // Get the product's production location printer (with restaurantId for job_type fallback)
    const printerName = await getProductPrinter(data.productId, data.restaurantId);
    
    if (!printerName) {
      console.log("[Cancellation] No production location printer configured for product");
      return false;
    }
    
    // Get printer settings
    const settings = await getPrinterSettings(printerName, data.restaurantId);
    const charsPerLine = settings?.chars_per_line || 48;
    
    // Format the ticket with template
    const content = formatCancellationKitchenTicket(data, charsPerLine, template);
    
    // Dispatch via centralized dispatcher
    return await dispatchPrint(data.restaurantId, printerName, content, {
      logType: "cancelamento",
      logIdentifier: data.productName,
      locationName: "Cancelamento",
      settings: settings || undefined,
    });
  } catch (error) {
    console.error("[Cancellation] Error printing to kitchen:", error);
    return false;
  }
}

/**
 * Print cancellation receipt to cashier
 */
export async function printCancellationReceipt(data: CancellationData): Promise<boolean> {
  try {
    // Fetch the template for cancellation receipts
    const template = await fetchPrintTemplate(data.restaurantId, "cancellation_receipt");
    
    // Try to get printer configured for cancellation_receipt job type first
    const { data: jobTypeData } = await supabase
      .from("printer_job_types")
      .select(`
        printer_id,
        printers!inner (
          id,
          printer_name,
          is_active,
          restaurant_id,
          paper_width,
          chars_per_line,
          auto_cut_enabled,
          cut_type,
          extra_lines_before_cut,
          protocol
        )
      `)
      .eq("job_type", "cancellation_receipt")
      .eq("is_enabled", true);

    // Filter by restaurant and active status
    const validPrinters = jobTypeData?.filter(
      (jt: any) => 
        jt.printers?.restaurant_id === data.restaurantId && 
        jt.printers?.is_active === true
    );

    let printerInfo: { printerName: string; settings: PrinterSettings } | null = null;

    if (validPrinters && validPrinters.length > 0) {
      const printer = validPrinters[0].printers;
      printerInfo = {
        printerName: printer.printer_name,
        settings: {
          paper_width: (printer.paper_width || "80mm") as "58mm" | "80mm",
          chars_per_line: printer.chars_per_line || 48,
          auto_cut_enabled: printer.auto_cut_enabled ?? true,
          cut_type: (printer.cut_type || "full") as "full" | "partial",
          extra_lines_before_cut: printer.extra_lines_before_cut ?? 3,
          protocol: (printer.protocol || "epson_standard") as "epson_standard" | "bematech_legacy" | "generic",
        },
      };
    } else {
      // Fallback to default printer
      printerInfo = await getDefaultPrinter(data.restaurantId);
    }
    
    if (!printerInfo) {
      console.log("[Cancellation] No printer configured for cancellation receipts");
      return false;
    }
    
    const charsPerLine = printerInfo.settings.chars_per_line;
    
    // Format the receipt with template
    const content = formatCancellationReceipt(data, charsPerLine, template);
    
    // Dispatch via centralized dispatcher
    return await dispatchPrint(data.restaurantId, printerInfo.printerName, content, {
      logType: "cancelamento_comprovante",
      logIdentifier: data.productName,
      locationName: "Cancelamento",
      settings: printerInfo.settings,
    });
  } catch (error) {
    console.error("[Cancellation] Error printing receipt:", error);
    return false;
  }
}

/**
 * Get restaurant's cancellation print settings
 */
export async function getCancellationPrintSettings(restaurantId: string): Promise<{
  printKitchen: boolean;
  printReceipt: boolean;
}> {
  try {
    const { data, error } = await supabase
      .from("restaurants")
      .select("print_cancellation_kitchen, print_cancellation_receipt")
      .eq("id", restaurantId)
      .single();
    
    if (error || !data) {
      return { printKitchen: true, printReceipt: false };
    }
    
    return {
      printKitchen: data.print_cancellation_kitchen ?? true,
      printReceipt: data.print_cancellation_receipt ?? false,
    };
  } catch {
    return { printKitchen: true, printReceipt: false };
  }
}
